import React from "react"

// Lightweight, paper-styled HTML mock of the generated PDF so admins can see
// branding/layout while editing. Not pixel-perfect to Prawn — a faithful
// approximation of structure and copy.

function normalizeColor(hex) {
  const v = String(hex || "").replace("#", "").trim()
  return /^[0-9a-fA-F]{6}$/.test(v) ? `#${v}` : "#111827"
}

const SAMPLE = {
  booking: {
    customer: "Rahul Mehta",
    rows: [
      ["Booking ID", "1042"],
      ["Booking Date", "12 Jul 2026"],
      ["Booking Amount", "₹ 12,50,000.00"],
      ["Project", "Skyline Residences"],
      ["Unit", "A-1203"],
      ["Type", "3 BHK"],
    ],
    total: ["Total Consideration Value", "₹ 1,25,00,000.00"],
    schedule: [
      ["On Booking", "10%", "₹ 12,50,000.00", "12 Jul 2026"],
      ["On Foundation", "40%", "₹ 50,00,000.00", "10 Oct 2026"],
      ["On Possession", "50%", "₹ 62,50,000.00", "12 Jul 2027"],
    ],
  },
  quotation: {
    customer: "Rahul Mehta",
    rows: [
      ["Base Price", "₹ 1,20,00,000.00"],
      ["Preferred Location Charges (PLC)", "₹ 3,00,000.00"],
      ["Car Parking", "₹ 2,00,000.00"],
    ],
    total: ["Total Consideration Value", "₹ 1,25,00,000.00"],
    schedule: [
      ["On Booking", "10%", "₹ 12,50,000.00", "12 Jul 2026"],
      ["On Foundation", "40%", "₹ 50,00,000.00", "10 Oct 2026"],
      ["On Possession", "50%", "₹ 62,50,000.00", "12 Jul 2027"],
    ],
  },
  demand_letter: {
    customer: "Rahul Mehta",
    rows: [
      ["Installment", "On Foundation"],
      ["Amount Due", "₹ 50,00,000.00"],
      ["Due Date", "10 Oct 2026"],
      ["Days Overdue", "14"],
    ],
  },
  receipt: {
    customer: "Rahul Mehta",
    rows: [
      ["Receipt No:", "RCT-1042"],
      ["Date:", "12 Jul 2026"],
      ["Payment Mode:", "NEFT"],
      ["Amount:", "₹ 12,50,000.00"],
    ],
  },
}

function Table({ rows, total }) {
  return (
    <table className="mt-2 w-full border-collapse text-[10px]">
      <tbody>
        {rows.map(([label, value], i) => (
          <tr key={i} className="border-b border-slate-200">
            <td className="px-2 py-1.5 font-medium text-slate-700">{label}</td>
            <td className="px-2 py-1.5 text-right text-slate-800">{value}</td>
          </tr>
        ))}
        {total && (
          <tr className="bg-slate-50 font-semibold">
            <td className="px-2 py-1.5 text-slate-900">{total[0]}</td>
            <td className="px-2 py-1.5 text-right text-slate-900">{total[1]}</td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

export default function DocumentPreview({ type, config, branding, orgName = "Your Company" }) {
  const accent = normalizeColor(branding?.accent_color)
  const sample = SAMPLE[type] || SAMPLE.quotation
  const contact = [branding?.address, branding?.phone, branding?.email, branding?.website]
    .map((v) => (v || "").trim())
    .filter(Boolean)
    .join("  •  ")
  const compliance = [branding?.gst_number && `GSTIN: ${branding.gst_number}`].filter(Boolean).join("    ")

  return (
    <div className="mx-auto w-full max-w-[520px] rounded-sm bg-white px-9 py-8 text-[11px] leading-relaxed text-slate-800 shadow-[0_1px_3px_rgba(0,0,0,0.12),0_8px_24px_-12px_rgba(0,0,0,0.25)] ring-1 ring-slate-200">
      {/* Letterhead */}
      <div style={{ color: accent }} className="text-[17px] font-bold">
        {orgName}
      </div>
      {compliance && <div className="text-[9px] text-slate-500">{compliance}</div>}
      {contact && <div className="text-[9px] text-slate-500">{contact}</div>}
      {config?.header && <div className="mt-1.5 text-[9px] italic text-slate-500">{config.header}</div>}
      <div className="mt-2 border-t border-slate-200" />

      {/* Title */}
      {config?.title && (
        <div className="mt-4 text-center text-[14px] font-bold tracking-wide text-slate-900">{config.title}</div>
      )}

      {/* Date + body */}
      <div className="mt-3 text-right text-[10px] text-slate-500">Date: 12 Jul 2026</div>

      {type !== "receipt" && (
        <p className="mt-2 text-[10px] text-slate-700">
          {type === "demand_letter" || type === "booking"
            ? `Dear ${sample.customer},`
            : `Prepared for ${sample.customer}`}
        </p>
      )}
      {config?.body && <p className="mt-1.5 text-[10px] text-slate-600">{config.body}</p>}

      {/* Body table */}
      {type === "quotation" || type === "booking" ? (
        <>
          <div className="mt-4 text-[11px] font-semibold text-slate-900">
            {type === "booking" ? "Booking Summary" : "Cost Breakup"}
          </div>
          <Table rows={sample.rows} total={sample.total} />
          <div className="mt-4 text-[11px] font-semibold text-slate-900">Payment Schedule (CLP)</div>
          <table className="mt-2 w-full border-collapse text-[9px]">
            <thead>
              <tr className="bg-slate-100 text-left font-semibold text-slate-700">
                <th className="px-2 py-1.5">Milestone</th>
                <th className="px-2 py-1.5 text-center">%</th>
                <th className="px-2 py-1.5 text-right">Amount</th>
                <th className="px-2 py-1.5">Due Date</th>
              </tr>
            </thead>
            <tbody>
              {sample.schedule.map((r, i) => (
                <tr key={i} className="border-b border-slate-200">
                  <td className="px-2 py-1.5">{r[0]}</td>
                  <td className="px-2 py-1.5 text-center">{r[1]}</td>
                  <td className="px-2 py-1.5 text-right">{r[2]}</td>
                  <td className="px-2 py-1.5">{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <Table rows={sample.rows} />
      )}

      {/* Terms */}
      {config?.terms && (
        <>
          {(type === "quotation" || type === "booking") && (
            <div className="mt-4 text-[10px] font-semibold text-slate-900">Terms &amp; Conditions</div>
          )}
          <p className="mt-1.5 text-[9px] text-slate-500">{config.terms}</p>
        </>
      )}

      {/* Signatory */}
      <div className="mt-10 flex justify-end">
        <div className="w-[180px] text-[10px]">
          <div className="text-slate-600">For {orgName}</div>
          <div className="mt-8 border-t border-slate-400 pt-1">
            <div className="font-semibold text-slate-900">{config?.signatory_name || "—"}</div>
            <div className="text-[9px] text-slate-500">{config?.signatory_designation || "Authorized Signatory"}</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 border-t border-slate-100 pt-2 text-center text-[8px] text-slate-400">
        {config?.footer || "This is a computer-generated document."}
      </div>
    </div>
  )
}
